// Phase 43 plan 43-09: hand-written, BFF-routed client for the instance-keyed approval-workflow
// contract (plans 43-01/43-04). Deliberately kept OUTSIDE src/api/generated/ (the same precedent
// features/kiosk/api/kioskArrival.ts established in plan 34-01, and plans 31-05/32-01 before it) so
// a later `npm run api:generate` regeneration — which requires a live Aspire stack — can never
// conflict with or overwrite this file. Do not move this into src/api/generated/ and do not hand-
// edit anything under that directory to replace it.
//
// Replaces the orval-generated (see 25-06-SUMMARY.md) approval-workflow client that talked to the
// retired Phase 23 per-stage routes (plan 43-06 deletes them). Every request here goes through
// `workflowInstance`, whose baseURL is /bff/api/v1/workflow — so every call is routed through the
// BFF, per CLAUDE.md's architecture rule.
import { workflowInstance } from '@api/generated/axios-instance';

export interface ApprovalVisitorDto {
  firstName: string;
  lastName: string;
  company: string | null;
  visitorTypeId: string | null;
}

export interface OpenApprovalDto {
  instanceId: string;
  appointmentId: string;
  title: string;
  scheduledAt: string;
  primaryHostName: string | null;
  locationName: string | null;
  waitingSince: string;
  visitors: ApprovalVisitorDto[];
}

export interface OpenApprovalsResponseDto {
  items: OpenApprovalDto[];
}

export interface MyApprovalDecisionDto {
  instanceId: string;
  appointmentId: string;
  decision: string;
  reason: string | null;
  decidedAt: string;
  decidedByDisplayName: string | null;
  title: string;
  scheduledAt: string;
  primaryHostName: string | null;
  locationName: string | null;
  visitors: ApprovalVisitorDto[];
}

export interface MyApprovalDecisionsResponseDto {
  items: MyApprovalDecisionDto[];
}

export interface ApprovalDecisionInfoDto {
  decision: string;
  reason: string | null;
  decidedAt: string;
  decidedByDisplayName: string | null;
}

export interface ApprovalInstanceDetailDto {
  id: string;
  appointmentId: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  decision: ApprovalDecisionInfoDto | null;
  title: string;
  scheduledAt: string;
  primaryHostName: string | null;
  locationName: string | null;
  visitors: ApprovalVisitorDto[];
}

export interface DecideApprovalRequest {
  decision: 'Approved' | 'Rejected';
  reason: string | null;
}

export interface DecideApprovalResponse {
  instanceStatus: string;
}

/** The approver's open inbox — GET /api/v1/approval-workflow/instances/open. Never 404s. */
export function getOpenApprovals(signal?: AbortSignal): Promise<OpenApprovalsResponseDto> {
  return workflowInstance<OpenApprovalsResponseDto>({
    url: '/api/v1/approval-workflow/instances/open',
    method: 'GET',
    signal,
  });
}

/** The caller's own past decisions, newest first — GET /api/v1/approval-workflow/instances/my-decisions. */
export function getMyApprovalDecisions(signal?: AbortSignal): Promise<MyApprovalDecisionsResponseDto> {
  return workflowInstance<MyApprovalDecisionsResponseDto>({
    url: '/api/v1/approval-workflow/instances/my-decisions',
    method: 'GET',
    signal,
  });
}

/**
 * Instance-keyed detail read (D-24) — GET /api/v1/approval-workflow/instances/{instanceId}.
 * 404 when the instance is out of the caller's scope or does not exist; both cases are
 * deliberately indistinguishable on the wire.
 */
export function getApprovalDetail(instanceId: string, signal?: AbortSignal): Promise<ApprovalInstanceDetailDto> {
  return workflowInstance<ApprovalInstanceDetailDto>({
    url: `/api/v1/approval-workflow/instances/${instanceId}`,
    method: 'GET',
    signal,
  });
}

/**
 * POST /api/v1/approval-workflow/instances/{instanceId}/decide.
 * 400 INVALID_DECISION / REJECT_REASON_TOO_SHORT / REJECT_REASON_TOO_LONG; 404; 409
 * APPROVAL_ALREADY_DECIDED / APPROVAL_NOT_OPEN.
 */
export function postDecideApproval(
  instanceId: string,
  data: DecideApprovalRequest
): Promise<DecideApprovalResponse> {
  return workflowInstance<DecideApprovalResponse>({
    url: `/api/v1/approval-workflow/instances/${instanceId}/decide`,
    method: 'POST',
    data,
  });
}
